import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, MapPin, Star } from "lucide-react";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";
import { Button } from "../components/ui/button";
import { WEB_PACKAGES } from "../mock";
import { townPath, TOWN_REVIEWS } from "../towns";

const setMeta = (selector, attr, value) => {
  const el = document.querySelector(selector);
  const prev = el?.getAttribute(attr);
  el?.setAttribute(attr, value);
  return () => prev != null && el.setAttribute(attr, prev);
};

const useTownSeo = (town) => {
  useEffect(() => {
    window.scrollTo(0, 0);
    const prevTitle = document.title;
    document.title = `Web Design ${town.name} | Websites from £99 | Andy Watson IT`;
    const desc = `Local web design in ${town.name}, Lanarkshire. Websites from £99, business websites, online shops and social media management for ${town.name} businesses. Free quote.`;
    const undo = [
      setMeta('meta[name="description"]', "content", desc),
      setMeta('link[rel="canonical"]', "href", `https://andywatsonit.co.uk/${town.slug}`),
      setMeta('meta[property="og:url"]', "content", `https://andywatsonit.co.uk/${town.slug}`),
      setMeta('meta[property="og:title"]', "content", document.title),
      setMeta('meta[property="og:description"]', "content", desc),
    ];
    return () => {
      document.title = prevTitle;
      undo.forEach((u) => u());
    };
  }, [town]);
};

export default function TownPage({ town }) {
  useTownSeo(town);
  return (
    <div className="relative bg-white text-slate-900">
      <Navbar />
      <main className="pt-32 pb-24" data-testid={`town-page-${town.slug}`}>
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 transition-colors">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
          <div className="mt-8">
            <span className="section-eyebrow">
              <MapPin className="h-3.5 w-3.5" /> {town.name}, Lanarkshire
            </span>
          </div>
          <h1 data-testid="town-page-heading" className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900 leading-[1.05]">
            Web Design {town.name}
          </h1>
          <div className="mt-6 space-y-4 text-lg text-slate-600 leading-relaxed">
            {town.intro.map((p) => <p key={p}>{p}</p>)}
          </div>
          <TownPrices town={town} />
          <TownReviews town={town} />
          <div className="mt-10 flex flex-wrap gap-3">
            <Button asChild className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-6 h-12 group">
              <a href="/#contact" data-testid="town-page-quote-btn">
                Get a free quote <ArrowRight className="ml-1.5 h-4 w-4 btn-arrow" />
              </a>
            </Button>
            <Button asChild variant="outline" className="rounded-full px-6 h-12 border-slate-300 hover:bg-slate-50">
              <a href="/#web-design" data-testid="town-page-packages-btn">See all packages</a>
            </Button>
          </div>
          <TownNearby town={town} />
        </div>
      </main>
      <Footer />
    </div>
  );
}

const TownPrices = ({ town }) => (
  <div className="mt-10 rounded-3xl border border-slate-200 bg-slate-50 p-6 lg:p-8">
    <h2 className="text-base md:text-lg font-semibold text-slate-900">
      Website prices for {town.name} businesses
    </h2>
    <ul className="mt-4 grid sm:grid-cols-2 gap-3">
      {WEB_PACKAGES.map((p) => (
        <li key={p.name} className="flex items-start gap-2 text-sm text-slate-700">
          <Check className="h-4 w-4 text-sky-600 mt-0.5 shrink-0" />
          <span>
            <strong className="text-slate-900">{p.tagline}</strong> — {p.suffix ? `${p.suffix} ` : ""}{p.price}
          </span>
        </li>
      ))}
    </ul>
  </div>
);

const TownReviews = ({ town }) => {
  const reviews = TOWN_REVIEWS[town.name];
  if (!reviews?.length) return null;
  return (
    <div className="mt-10" data-testid="town-reviews">
      <h2 className="text-base md:text-lg font-semibold text-slate-900">What {town.name} businesses say</h2>
      <div className="mt-4 grid sm:grid-cols-2 gap-4">
        {reviews.map((r) => (
          <figure key={r.name + r.text} className="rounded-3xl border border-slate-200 bg-white p-6">
            <div className="flex gap-0.5">
              {Array.from({ length: r.rating || 5 }).map((_, i) => (
                <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
              ))}
            </div>
            <blockquote className="mt-3 text-slate-700 leading-relaxed">&ldquo;{r.text}&rdquo;</blockquote>
            <figcaption className="mt-4 text-sm">
              <span className="font-semibold text-slate-900">{r.name}</span>
              {r.business && <span className="text-slate-500"> · {r.business}</span>}
            </figcaption>
          </figure>
        ))}
      </div>
    </div>
  );
};

const TownNearby = ({ town }) => (
  <div className="mt-14 pt-8 border-t border-slate-100">
    <h2 className="text-base md:text-lg font-semibold text-slate-900">Web design near {town.name}</h2>
    <div className="mt-4 flex flex-wrap gap-2">
      {town.nearby.map((n) => (
        <Link
          key={n}
          to={townPath(n)}
          data-testid={`town-nearby-link-${n.toLowerCase().replace(/\s+/g, "-")}`}
          className="px-3.5 py-2 rounded-full border border-slate-200 text-sm text-slate-700 hover:border-sky-300 hover:text-sky-700 transition-colors"
        >
          Web design {n}
        </Link>
      ))}
    </div>
  </div>
);
